<script lang="ts" setup>
interface Props {
  mediaLink: string
  usePlayButton: boolean
  colorOfPlayButton: string
  altText: string
}

const props = defineProps<Props>()
const video = ref<HTMLVideoElement | null>(null)
const playing = ref<boolean>(false)

// Extract this logic to normalize function after 1st of August 2023 release
const { mediaLink, colorOfPlayButton, usePlayButton, altText } = props

const textColor = colorOfPlayButton === 'primary' ? 'black' : 'white'

const videoRegex = /[^\s]+(.*?).(mp4|avi|wmv|mov|flv|mkv|webm|vob|ogv|m4v|3gp|3g2|mpeg|mpg|m2v|m4v|svi|3gpp|3gpp2|mxf|roq|nsv|flv|f4v|f4p|f4a|f4b)$/

function playHandler() {
  if (video.value) {
    if (video.value.paused) {
      video.value.play()
      playing.value = true
    }
    else {
      video.value.pause()
      playing.value = false
    }
  }
}

const stopPlaying = () => (playing.value = false)

const isVideo = mediaLink.match(videoRegex)
</script>

<template>
  <section class="media-component">
    <div class="edge wave" />
    <div v-if="isVideo" class="media-component__video-container">
      <video
        ref="video"
        class="media-component__video"
        :autoplay="!usePlayButton"
        :loop="!usePlayButton"
        muted
        @ended="stopPlaying"
        @dblclick="playHandler"
      >
        <source :src="mediaLink" :type="`video/${isVideo[2]}`">
      </video>
      <button
        v-if="usePlayButton"
        :class="`media-component__play ${
          playing === true ? 'media-component__play--playing' : 'media-component__play--paused'
        }`"
        :style="{
          '-webkit-mask-image': `url('/icons/${playing === true ? 'pause' : 'play'}.svg')`,
          'backgroundColor': textColor,
        }"
        @click="playHandler"
      />
    </div>
    <ImageResponsive
      v-else
      provider="storyblok"
      class="img"
      :src="mediaLink"
      :alt="altText"
      :lazy="true"
    />
  </section>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';
@import 'assets/scss/rules/edges';

.media-component {
  position: relative;

  &__video-container {
    position: relative;
  }

  &__video {
    width: 100%;
  }

  &__play {
    position: absolute;

    right: 9.6rem;
    bottom: 3.2rem;

    @media (max-width: $small-breakpoint) {
      right: 1.6rem;
      bottom: 4.8rem;
    }

    &--playing {
      width: 2rem;
      height: 2rem;
    }
    &--paused {
      width: 1.6rem;
      height: 2rem;
    }
  }
}

.edge {
  position: absolute;
  top: -1.5rem;
  left: 0;
  width: 100%;
  height: 1.5rem;
  background: inherit;
  border: none;
}
.edge.wave {
  @include wave;
}
</style>
